/**
 * Insights hub card for TIN & NPI Performance: live summary on the face
 * (TIN / clinician counts + how many TINs sit below the ACO composite), click
 * to open the report. Mirrors RosterExposureCard's pattern.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart } from 'lucide-react';
import { ReportCard } from '@/components/insights/ReportCard';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { useAcoTinNpiPerformance } from '@/hooks/queries/useMipsQueries';
import { useAppPlusScoring } from '../lib/useAppPlusScoring';

export function TinNpiPerformanceCard() {
  const navigate = useNavigate();
  const { selectedAco } = useAco();
  const { programYear } = useProgramYear();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);

  const lane = useMemo(() => {
    const set = new Set<string>();
    for (const byType of Object.values(scoring.calculated)) for (const k of Object.keys(byType ?? {})) set.add(k);
    const order = ['Medicare CQM', 'MIPS CQM', 'eCQM'];
    return Array.from(set).sort((a, b) => (order.indexOf(a) + 99) % 99 - (order.indexOf(b) + 99) % 99)[0] ?? null;
  }, [scoring.calculated]);

  const perfQ = useAcoTinNpiPerformance(selectedAco?.id ?? null, programYear, lane, !!lane);
  const data = perfQ.data ?? null;
  const pending = (scoring.loading || (perfQ.isPending && !perfQ.data)) && !!lane;

  const npis = data ? data.npis.length + data.rosterOnly.length : 0;
  const unmapped = data ? data.npis.filter(n => n.npi === '0000000000').length : 0;

  const preview = pending ? (
    <div className="w-full py-6 text-center text-sm text-muted-foreground">Loading…</div>
  ) : (
    <div className="w-full py-2">
      <div className="flex items-center justify-around">
        <div className="text-center">
          <p className="text-4xl font-bold tabular-nums">{data ? data.tins.length : '—'}</p>
          <p className="mt-1 text-xs text-muted-foreground">Participant TINs</p>
        </div>
        <div className="h-16 w-px bg-border" />
        <div className="text-center">
          <p className="text-4xl font-bold tabular-nums">{data ? npis : '—'}</p>
          <p className="mt-1 text-xs text-muted-foreground">Clinicians</p>
        </div>
      </div>
      <p className="mt-3 min-h-[1rem] border-t pt-2 text-center text-xs text-muted-foreground">
        {!lane
          ? 'No calculated claims yet — upload data to populate'
          : unmapped > 0 && npis === unmapped
            ? 'NPI column was not mapped on upload — re-upload with NPI mapped for clinician detail'
            : data?.rosterImported
              ? `${lane} · roster names & QP status applied`
              : `${lane} · import the ACO roster for names and QP status`}
      </p>
    </div>
  );

  return (
    <ReportCard
      title="TIN & NPI Performance"
      description="Participant practices ranked against the ACO composite, expanding to clinician-level measure detail. Bars first, table as the alternate view."
      icon={<LineChart className="h-6 w-6" />}
      onClick={() => navigate('/programs/app-plus/insights/tin-npi')}
      status="available"
      preview={preview}
    />
  );
}
